import { useLayoutEffect, useState, useEffect, Dispatch } from "react";
import { AxiosError } from "axios";

import { ISegment } from "../../Models/Models";
import { IChartMergedDatapoint } from "../../Models/Models";
import {
    SET_LOADING_MOSAIC_SEGMENTS,
    GET_MOSAIC_SEGMENTS_SUCCESS,
} from "../../Actions/Types";
import { RetailMatchAxios } from "../../Contracts/Axios/RetailMatchAxios";
import { ICoordinate } from "../../Models/ICoordinate";
import { notification } from "antd";

export const useOutsideClick = (ref, callback: () => void) => {
    const handleClick = (e) => {
        if (ref.current && !ref.current.contains(e.target)) {
            callback();
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClick);

        return () => {
            document.removeEventListener("click", handleClick);
        };
    });
};

export const useWindowResize = (): {
    width: number;
    height: number;
} => {
    /** state */
    const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
    /** effects */
    useLayoutEffect(() => {
        let resizeId;
        const doneResizing = ():void => {
            clearTimeout(resizeId);
            resizeId = setTimeout(updateSize, 500);
        };
        const updateSize = ():void => {
            const currentWidth: number = window.innerWidth > 0
                ? window.innerWidth
                : screen.width;
            const currentHeight: number = window.innerHeight > 0
                ? window.innerHeight
                : screen.height;

            const newSize = {
                width: currentWidth,
                height: currentHeight,
            };
            setWindowSize(newSize);
            (window as any).windowSize = newSize;
        };

        window.addEventListener("resize", doneResizing);
        updateSize();
        return () => window.removeEventListener("resize", updateSize);
    }, []);

    return windowSize;
};

export const popupCenter = (url = "", title = "", w = 700, h = 700): Window => {
    // Fixes dual-screen position                         Most browsers      Firefox
    const dualScreenLeft
    = window.screenLeft != undefined ? window.screenLeft : window.screenX;
    const dualScreenTop
    = window.screenTop != undefined ? window.screenTop : window.screenY;

    const width = window.innerWidth
        ? window.innerWidth
        : document.documentElement.clientWidth
            ? document.documentElement.clientWidth
            : screen.width;
    const height = window.innerHeight
        ? window.innerHeight
        : document.documentElement.clientHeight
            ? document.documentElement.clientHeight
            : screen.height;

    const systemZoom = width / window.screen.availWidth;
    const left = (width - w) / 2 / systemZoom + dualScreenLeft;
    const top = (height - h) / 2 / systemZoom + dualScreenTop;

    const newWindow = window.open(
        url,
        title,
        "scrollbars=yes, location=0, personalbar=0, titlebar=0, menubar=0, toolbar=0, width="
      + w / systemZoom
      + ", height="
      + h / systemZoom
      + ", top="
      + top
      + ", left="
      + left
    );

    return newWindow;
};

// ███████╗ ██████╗ ██████╗ ████████╗
// ██╔════╝██╔═══██╗██╔══██╗╚══██╔══╝
// ███████╗██║   ██║██████╔╝   ██║
// ╚════██║██║   ██║██╔══██╗   ██║
// ███████║╚██████╔╝██║  ██║   ██║
// ╚══════╝ ╚═════╝ ╚═╝  ╚═╝   ╚═╝

export const sortMergedChartData = (
    chartData: IChartMergedDatapoint[],
    convertToUsableNumber = false,
    customSort?: (a, b) => 1 | -1 | 0
): IChartMergedDatapoint[] => {
    return sorting(chartData, convertToUsableNumber, customSort);
};

const sorting = (
    chartData: IChartMergedDatapoint[],
    convertToUsableNumber = false,
    customSort?: (a, b) => 1 | -1 | 0
): any => {
    // sort numeric
    if (convertToUsableNumber) {
        chartData?.sort((a, b) =>
            Number(formatToUsableNumber(a.id)) > Number(formatToUsableNumber(b.id))
                ? 1
                : Number(formatToUsableNumber(b.id))
          > Number(formatToUsableNumber(a.id))
                    ? -1
                    : 0
        );
    // custom sort
    } else if (customSort) {
        chartData?.sort(customSort);
    // sort alphabetic
    } else {
        chartData?.sort((a, b) => (a.id > b.id ? 1 : b.id > a.id ? -1 : 0));
    }
    return chartData;
};

const UYNSortOrder = {
    Y: 0,
    N: 1,
    U: 2,
};

export const sortUYN = (a, b) => {
    return UYNSortOrder[a.id] > UYNSortOrder[b.id] ? 1 : -1;
};

const DwellingTypeSortOrder = {
    "Multi Family Dwelling Unit": 1,
    "Single Family Dwelling Unit": 0,
};

export const sortDwellingType = (a, b) => {
    return DwellingTypeSortOrder[a.id] > DwellingTypeSortOrder[b.id] ? 1 : -1;
};

const educationSortOrder = {
    "Less Than High School Diploma": 0,
    "High School Diploma": 1,
    "Some College": 2,
    "Bachelor Degree": 3,
    "Graduate Degree": 4,
};

export const sortEducation = (a, b) => {
    return educationSortOrder[a.id] > educationSortOrder[b.id] ? 1 : -1;
};

// ███████╗ ██████╗ ██████╗ ███╗   ███╗ █████╗ ████████╗
// ██╔════╝██╔═══██╗██╔══██╗████╗ ████║██╔══██╗╚══██╔══╝
// █████╗  ██║   ██║██████╔╝██╔████╔██║███████║   ██║
// ██╔══╝  ██║   ██║██╔══██╗██║╚██╔╝██║██╔══██║   ██║
// ██║     ╚██████╔╝██║  ██║██║ ╚═╝ ██║██║  ██║   ██║
// ╚═╝      ╚═════╝ ╚═╝  ╚═╝╚═╝     ╚═╝╚═╝  ╚═╝   ╚═╝

const formatToUsableNumber = (str: string): string => {
    const cleanedStr = str.replace(/([K])|\+|\$/g, "");
    const n = cleanedStr.indexOf(" ");
    return cleanedStr.substring(0, n != -1 ? n : cleanedStr.length);
};

export const formatUYN = (id: string): string => {
    return id === "Y" ? `Yes` : id === "N" ? `No` : `Unknown`;
};

export const formatMarriedSingle = (id: string): string => {
    return id === "Married" ? `Yes` : id === "Single" ? `No` : `Unknown`;
};

const dwellingFormat = {
    "Multi Family Dwelling Unit": "Multi Family",
    "Single Family Dwelling Unit": "Single Family",
};

export const formatDwelling = (id: string): string => {
    const returnVal = dwellingFormat[id];
    return returnVal;
};

const educationFormat = {
    "Less Than High School Diploma": "< HS Diploma",
    "High School Diploma": "HS Diploma",
    "Some College": "Some College",
    "Bachelor Degree": "Bachelor Degree",
    "Graduate Degree": "Graduate Degree",
};

export const formatEducation = (id: string): string => {
    const returnVal = educationFormat[id];
    return returnVal;
};

export const format24Hr = (h24: string): string => {
    const h = Number(h24) % 12;
    return `${h === 0 ? 12 : h}${Number(h24) >= 12 ? "pm" : "am"}`;
};

export const formatDOW = (d: string): string => {
    const dow = ["Sun", "Mon", "Tue", "Wed", "Thurs", "Fri", "Sat"];
    return dow[Number(d)];
};

export const formatWeek = (dateString: string): string => {
    const dateObj = new Date(dateString);
    return `${
        Number(dateObj.getMonth()) + 1
    }/${dateObj.getDate()}/${dateObj.getFullYear()}`;
};
/** GB: NOTE!
 * Parameter initialization like the below will only work if the parameter is EXACTLY
 * undefined. If null is passed in, the parameter initializer will not take effect.
 * This check allows this function to not blow things up, but of course the bigger issue is
 * the component that ends up calling this with null.
 */
export const getPrettyNumber = (value = 0, decimalPlaces: number): string => {
    if (value == null) value = 0;
    return Number(value.toFixed(decimalPlaces)).toLocaleString();
};

export const formatYValue = (num: number): string => {
    return `${Math.round(Number(num) * 10000) / 100}%`;
};

export const formatLabelWithEllipsis = (str: string, length = 20): string => {
    return str.length >= length ? `${str.substr(0, length - 1)}...` : str;
};

export const getPolygonCentroid = (points): ICoordinate => {
    const centroid: ICoordinate = {
        latitude: 0,
        longitude: 0,
    };

    for (const point of points) {
        centroid.latitude += point.y;
        centroid.longitude += point.x;
    }

    centroid.latitude /= points.length;
    centroid.longitude /= points.length;

    return centroid;
};

const setLoadingMosaicSegments = (loading: boolean) => ({
    type: SET_LOADING_MOSAIC_SEGMENTS,
    data: loading,
});

const getMosaicSegmentsSuccess = (data: ISegment[]) => ({
    type: GET_MOSAIC_SEGMENTS_SUCCESS,
    data: data,
});

export const getMosaicSegments = () => {
    return (dispatch: Dispatch<any>) => {
        dispatch(setLoadingMosaicSegments(true));

        const retailMatchAxios = new RetailMatchAxios();
        retailMatchAxios
            .GetMosaicSegments()
            .then((response: ISegment[]) => {
                dispatch(getMosaicSegmentsSuccess(response));
            })
            .catch((error: AxiosError) => {
                notification.error({
                    description: "Error",
                    message:
                        "Failed to get mosaic segment information. Please contact SCOUT Support.",
                    duration: null, // user must close
                });
            })
            .finally(() => {
                dispatch(setLoadingMosaicSegments(false));
            });
    };
};

export const firstLetterCapitalizer = (inputString: string): string => {
    const regexPattern = /(\b[a-z](?!\s))/g;

    inputString = inputString.toLowerCase();
    inputString = inputString.replace(regexPattern, (callbackString: string) =>
        callbackString.toUpperCase()
    );

    return inputString;
};

export const shortenStr = (str: string, n: number): string => {
    return str?.length > n ? str.substr(0, n - 1) + "..." : str;
};

export const determinePropensityText = (str: "P" | "S" | "N" | "T"): string => {
    switch (str) {
        case "P":
            return "Very High";
        case "S":
            return "High";
        default:
            return "Average";
    }
};
